import { Link, useLocation } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import React, {
	Dispatch,
	SetStateAction,
	useEffect,
	useRef,
	useState,
    useMemo,
} from 'react';


interface Tab {
	id: string;
	label: string;
	icon?: React.ReactNode;
	disabled?: boolean;
	badge?: string | number;
	path?: string;
	content?: React.ReactNode;
}

interface TabAction {
	label: string;
	icon?: string;
	onClick: () => void;
	className?: string;
	variant?: 'primary' | 'secondary' | 'outline' | 'danger';
}

interface TabComponentProps {
	tabs?: Tab[];
	activeTab?: string;
	setActiveTab?: (tab: string) => void;
	onTabChange?: (tabId: string) => void;
	actions?: TabAction[];
	children?: React.ReactNode;
	showContent?: boolean;
	usePathname?: boolean;
	contentClassName?: string;
	containerClassName?: string;
	showIndicator?: boolean;
	variant?: 'link' | 'button';
    
    // Legacy support props
    components?: Record<string, any>;
    setTab?: (tabName: string) => void;
}

export default function TabComponent({
	tabs,
	activeTab,
	setActiveTab,
	onTabChange,
	actions,
	children,
	showContent = true,
	usePathname: usePathnameActive = false,
	contentClassName = '',
	containerClassName = '',
	showIndicator = true,
	variant = 'button',
    components,
    setTab,
}: TabComponentProps) {
	const location = useLocation();
    const pathname = location.pathname;
	const tabsRef = useRef<(HTMLButtonElement | HTMLAnchorElement | null)[]>([]);

    // Map legacy components to new tabs array
    const resolvedTabs: Tab[] = useMemo(() => {
		if (tabs) return tabs;
		if (components) {
			return Object.keys(components).map((key) => {
                const Component = components[key];
                return {
                    id: key,
                    label: key,
                    content: Component ? <Component /> : null,
                };
            });
		}
		return [];
	}, [tabs, components]);

	const [currentActiveTab, setCurrentActiveTab] = useState<string>(
		activeTab || resolvedTabs[0]?.id || '',
	);
    
    // Legacy setTab initialization tracking
    useEffect(() => {
        if (components && resolvedTabs.length > 0 && !activeTab && !currentActiveTab) {
            setCurrentActiveTab(resolvedTabs[0].id);
            setTab && setTab(resolvedTabs[0].id);
        }
    }, [components, resolvedTabs]);

	const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

	// Determine which active tab state to use
	const resolvedActiveTab =
		activeTab !== undefined ? activeTab : currentActiveTab;

	// Handle pathname-based activation
	useEffect(() => {
		if (usePathnameActive && resolvedTabs.length > 0) {
			const pathnameTab = pathname.split('/').pop() || '';
			const matchedTab = resolvedTabs.find(
				(tab) => tab.path === pathname || tab.id === pathnameTab,
			);
			if (matchedTab) {
				handleTabChange(matchedTab.id);
			}
		}
	}, [pathname, usePathnameActive, resolvedTabs]);

	// Update indicator position
	useEffect(() => {
		const activeTabIndex = resolvedTabs.findIndex(
			(tab) => tab.id === resolvedActiveTab,
		);
		const activeTabElem = tabsRef.current[activeTabIndex];

		if (activeTabElem && showIndicator) {
			setIndicatorStyle({
				left: activeTabElem.offsetLeft,
				width: activeTabElem.offsetWidth,
			});
		}
	}, [resolvedActiveTab, resolvedTabs, showIndicator]);

	const handleTabChange = (tabId: string) => {
		const tab = resolvedTabs.find((t) => t.id === tabId);
		if (tab?.disabled) return;

		if (onTabChange) {
			onTabChange(tabId);
		} else if (setActiveTab) {
			setActiveTab(tabId);
		} else {
			setCurrentActiveTab(tabId);
		}
        
        // Legacy callback
        if (setTab) {
            setTab(tabId);
        }
	};

	const activeTabData = resolvedTabs.find((tab) => tab.id === resolvedActiveTab);
	const activeContent = activeTabData?.content || children;
	const activeActions =
		actions?.filter((action) => {
			if (!activeTabData) return false;
			return true;
		}) || [];

	// Link-based variant
	if (variant === 'link') {
		return (
			<div
				className={`w-full flex flex-col animate-in fade-in duration-500 ${containerClassName}`}>
				{/* Tab Header & Actions */}
				<div className='flex flex-col lg:flex-row lg:items-center justify-between gap-2 py-3 sm:px-5 border-b border-neutral-100 bg-white sticky top-16 z-10'>
					{/* Tab List */}
					<div className='flex items-center p-1 bg-neutral-100/80 rounded-lg w-full lg:w-fit overflow-x-auto hide-scrollbar select-none gap-1'>
						{resolvedTabs.map((tab, index) => {
							const isActive = resolvedActiveTab === tab.id;
							return (
                                // @ts-ignore
								<Link
									key={tab.id}
									to={tab.path || '#'}
                                    ref={(el: any) => {
                                        tabsRef.current[index] = el;
                                    }}
									className={`
										flex items-center justify-center gap-2 px-3 h-7 rounded-md text-xs font-bold uppercase tracking-wider transition-all duration-300 whitespace-nowrap
										${
											isActive
												? 'bg-white text-neutral-900 shadow-sm ring-1 ring-neutral-200/50'
												: tab.disabled
													? 'text-neutral-400 cursor-not-allowed pointer-events-none'
													: 'text-neutral-500 hover:text-neutral-700 hover:bg-white/40'
										}
									`}>
									{tab.icon && <span>{tab.icon}</span>}
									{tab.label}
									{tab.badge && (
										<span
											className={`
												ml-1 px-2 py-0.5 rounded-full text-xs 
												${isActive ? 'bg-[#0898a0] text-white' : 'bg-neutral-200 text-neutral-600'}
											`}>
											{tab.badge}
										</span>
									)}
								</Link>
							);
						})}
					</div>

					{/* Contextual Actions */}
					{activeActions.length > 0 && (
						<div className='flex items-center gap-2 flex-wrap justify-start lg:justify-end'>
							{activeActions.map((action, index) => (
								<Button
									key={`${resolvedActiveTab}-${index}`}
									variant={action.variant || 'primary'}
									size='sm'
									onClick={action.onClick}
									className={`font-bold transition-all shadow-sm ${action.className || ''}`}
									leftIcon={
										action.icon ? (
											<img
												src={action.icon}
												width={16}
												height={16}
												alt={action.label}
											/>
										) : undefined
									}>
									{action.label}
								</Button>
							))}
						</div>
					)}
				</div>

				{/* Tab Content */}
				{showContent && activeContent && (
					<div
						className={`flex-1 w-full bg-neutral-50/30 p-6 min-h-[70vh] animate-in fade-in ${contentClassName}`}>
						{activeContent}
					</div>
				)}
			</div>
		);
	}

	// Button-based variant (default)
	return (
		<div className={`w-full flex flex-col h-full gap-5 ${containerClassName}`}>
			{/* Tab Header with Indicator */}
			<div className='w-full border-b border-neutral-100'>
				<div className='relative flex items-center overflow-x-auto hide-scrollbar'>
					<div className='relative flex w-full space-x-1 px-1'>
						{resolvedTabs.map((tab, index) => (
							<button
								key={tab.id}
								ref={(el) => {
									tabsRef.current[index] = el;
								}}
								onClick={() => handleTabChange(tab.id)}
								disabled={tab.disabled}
								className={`
									flex items-center gap-2 px-4 py-3 font-medium transition-all duration-200 whitespace-nowrap relative z-10 uppercase text-xs tracking-wider
									${
										resolvedActiveTab === tab.id
											? 'text-[#0898a0] font-bold'
											: tab.disabled
												? 'text-neutral-400 cursor-not-allowed'
												: 'text-neutral-500 hover:text-neutral-700'
									}
								`}
								aria-selected={resolvedActiveTab === tab.id}
								role='tab'>
								{tab.icon && (
									<span className='shrink-0'>{tab.icon}</span>
								)}
								<span>{tab.label}</span>
								{tab.badge && (
									<span
										className={`
											ml-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0
											${
												resolvedActiveTab === tab.id
													? 'bg-[#0898a0] text-white'
													: 'bg-neutral-200 text-neutral-600'
											}
										`}>
										{tab.badge}
									</span>
								)}
							</button>
						))}

						{/* Animated Indicator */}
						{showIndicator && (
							<div
								className='absolute bottom-0 h-1 bg-[#0898a0] transition-all duration-300 rounded-full'
								style={{
									left: `${indicatorStyle.left}px`,
									width: `${indicatorStyle.width}px`,
								}}
							/>
						)}
					</div>
				</div>
			</div>

			{/* Tab Content */}
			{showContent && activeContent && (
				<div
					className={`w-full flex flex-auto flex-grow h-full animate-in fade-in ${contentClassName}`}>
					{activeContent}
				</div>
			)}

			{/* Contextual Actions */}
			{activeActions.length > 0 && (
				<div className='flex items-center gap-2 flex-wrap justify-end'>
					{activeActions.map((action, index) => (
						<Button
							key={`${resolvedActiveTab}-${index}`}
							variant={action.variant || 'primary'}
							size='sm'
							onClick={action.onClick}
							className={`font-bold transition-all shadow-sm ${action.className || ''}`}
							leftIcon={
								action.icon ? (
									<img
										src={action.icon}
										width={16}
										height={16}
										alt={action.label}
									/>
								) : undefined
							}>
							{action.label}
						</Button>
					))}
				</div>
			)}
		</div>
	);
}
